import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TagModel, TagType, getTagNameByType } from '../../../shared/types/tag'
import { Grid, CircularProgress, Card, CardContent, Typography, ListItem, TextField, InputAdornment, IconButton, Menu, MenuItem, Badge, Checkbox } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import SearchIcon from '@material-ui/icons/Search'
import FilterListIcon from '@material-ui/icons/FilterList'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    input: {
        backgroundColor: '#f5f5f5', 
        borderRadius: theme.shape.borderRadius, 
        overflow: 'hidden',
        border: '1px solid #ddd',
        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5', 
            borderRadius: theme.shape.borderRadius, 
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none', 
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px', 
            fontSize: '0.9rem', 
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface TagsListProps {
    tags: TagModel[]
    selectedTag?: TagModel
    search: string
    onSearchChange(value: string): void
    typeFilter: number[]
    onTypeFilterChange(value: number[]): void
    onTagSelected(tag: TagModel): void
}

const typeFilterOptions = [TagType.Charger, TagType.Location, TagType.User, TagType.ChargingProfile]

const TagList: SFC<TagsListProps> = ({ tags, selectedTag, search, onSearchChange, typeFilter, onTypeFilterChange, onTagSelected }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const tagsExecuted = useSelector((state: any) => state.tags.items.all.executed)

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearchChange(e.target.value)
    }

    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper')

    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)

    const toggleTypeFilter = (value: number) => {
        if (typeFilter.includes(value)) {
            onTypeFilterChange(typeFilter.filter((x) => x !== value))
        } else {
            onTypeFilterChange([...typeFilter, value])
        }
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12} className={classes.search}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <TextField
                                fullWidth
                                variant="filled"
                                placeholder={t('tags.search')}
                                value={search}
                                onChange={handleSearchChanged}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <IconButton onClick={(e) => setFilterAnchorEl(e.currentTarget)} style={{ marginLeft: 4 }}>
                            <Badge badgeContent={typeFilter.length} color="secondary" invisible={typeFilter.length === 0}>
                                <FilterListIcon style={{ color: typeFilter.length > 0 ? '#6100ED' : undefined }} />
                            </Badge>
                        </IconButton>
                        <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
                            {typeFilterOptions.map((option) => (
                                <MenuItem key={option} onClick={() => toggleTypeFilter(option)}>
                                    <Checkbox
                                        checked={typeFilter.includes(option)}
                                        style={{ color: '#6100ED', padding: '4px' }}
                                    />
                                    {getTagNameByType(option)}
                                </MenuItem>
                            ))}
                        </Menu>
                        <div style={{ width: '32px', textAlign: 'center' }}>
                            {!tagsExecuted && <CircularProgress size={24} />}
                        </div>
                    </div>
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {tagsExecuted && tags.length === 0 ? (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('tags.noTags')} />
                        </div>
                    ) : (
                        tags.map((tag) => (
                            <Card key={tag.id} className={classes.listItem} elevation={1}>
                                <ListItem
                                    button
                                    // ZMENA: Porovnávame ID namiesto celých objektov pre bezpečné označenie aktívneho riadku
                                    selected={tag.id === selectedTag?.id}
                                    onClick={() => onTagSelected(tag)}
                                    className={classes.listItemButton}
                                >
                                    <div className={classes.listContentWrapper}>
                                        <CardContent className={classes.listContent}>
                                            <Typography variant="h6">{tag.name}</Typography>
                                            <Typography component="div" variant="caption">
                                                {getTagNameByType(tag.type)}
                                            </Typography>
                                        </CardContent>
                                    </div>
                                </ListItem>
                            </Card>
                        ))
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default TagList